// src/pages/contrats/NouveauContrat.jsx
import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Alert, Spinner, 
    InputGroup, Badge, Modal 
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { API_BASE_URL } from "../../constants/api";

// Composant Toast personnalisé (fixe en bas à droite)
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    useEffect(() => {
        if (show && autoHideDelay) {
            const timer = setTimeout(() => {
                onClose();
            }, autoHideDelay);
            return () => clearTimeout(timer);
        }
    }, [show, autoHideDelay, onClose]);

    if (!show) return null;

    const toastConfig = {
        success: { bgColor: '#10b981', icon: 'ri-checkbox-circle-fill' },
        error: { bgColor: '#ef4444', icon: 'ri-error-warning-fill' },
        info: { bgColor: '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: '#f59e0b', icon: 'ri-alert-fill' }
    };

    const config = toastConfig[type] || toastConfig.info;

    return (
        <div className="toast-notification-fixed" style={{ 
            position: 'fixed', 
            bottom: '20px', 
            right: '20px', 
            zIndex: 9999 
        }}>
            <div className="toast-modern" style={{ 
                background: 'white', 
                borderRadius: '16px', 
                boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                minWidth: '320px',
                maxWidth: '400px',
                overflow: 'hidden',
                animation: 'slideInRight 0.3s ease-out'
            }}>
                <div className="toast-header-custom" style={{ 
                    background: config.bgColor, 
                    padding: '12px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer' }}>
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom" style={{ padding: '16px 20px' }}>
                    <p className="mb-0" style={{ color: '#1e293b', fontSize: '0.9rem' }}>{message}</p>
                </div>
                <div className="toast-progress" style={{ 
                    height: '3px', 
                    background: config.bgColor,
                    width: '100%',
                    animation: `shrink ${autoHideDelay}ms linear forwards`
                }}></div>
            </div>
        </div>
    );
};

// Barre d'alerte fixe en haut
const FixedAlertBar = ({ alerts, onClose }) => {
    if (!alerts || alerts.length === 0) return null;
    
    return (
        <div className="fixed-alert-bar" style={{
            position: 'sticky',
            top: '70px',
            zIndex: 1000,
            marginBottom: '1rem'
        }}>
            {alerts.map((alert, index) => (
                <Alert 
                    key={index}
                    variant={alert.type} 
                    onClose={() => onClose(index)} 
                    dismissible
                    className="mb-2 alert-modern shadow-sm"
                    style={{ borderRadius: '12px' }}
                >
                    <div className="d-flex align-items-center">
                        <i className={`${alert.icon} me-2 fs-5`}></i>
                        <div>
                            <strong>{alert.title}</strong>
                            <p className="mb-0 small">{alert.message}</p>
                        </div>
                    </div>
                </Alert>
            ))}
        </div>
    );
};

export default function NouveauContrat() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [showPreview, setShowPreview] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });
    const [fixedAlerts, setFixedAlerts] = useState([]);
    
    const [contrat, setContrat] = useState({
        employeur_nom: '',
        employeur_adresse: '',
        employeur_email: '',
        employeur_telephone: '',
        employe_nom: '',
        employe_prenom: '',
        employe_email: '',
        employe_telephone: '',
        employe_adresse: '',
        date_debut: '',
        date_fin: '',
        lettre_fin_contrat: null,
        certificat_travail: null
    });

    // Nettoyer les alertes fixes après un délai
    useEffect(() => {
        if (fixedAlerts.length > 0) {
            const timers = fixedAlerts.map((_, index) => {
                return setTimeout(() => {
                    setFixedAlerts(prev => prev.filter((_, i) => i !== index));
                }, 8000);
            });
            return () => timers.forEach(timer => clearTimeout(timer));
        }
    }, [fixedAlerts]);

    const showToast = (type, title, message) => {
        setToast({ show: true, type, title, message });
    };

    const hideToast = () => {
        setToast({ show: false, type: '', title: '', message: '' });
    };

    const addFixedAlert = (type, title, message) => {
        const iconMap = {
            success: 'ri-checkbox-circle-line',
            error: 'ri-error-warning-line',
            warning: 'ri-alert-line',
            info: 'ri-information-line'
        };
        setFixedAlerts(prev => [...prev, { 
            type, 
            title, 
            message, 
            icon: iconMap[type] || 'ri-information-line' 
        }]);
    };

    const removeFixedAlert = (index) => {
        setFixedAlerts(prev => prev.filter((_, i) => i !== index));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setContrat(prev => ({ ...prev, [name]: value }));
    };

const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
        if (file.size > 5 * 1024 * 1024) {
            addFixedAlert('error', 'Fichier trop volumineux', 'Le fichier ne doit pas dépasser 5 Mo');
            return;
        }
        // Stocker directement le fichier (objet File)
        setContrat(prev => ({ ...prev, [field]: file }));
        showToast('success', 'Fichier ajouté', `Le document a été chargé avec succès`);
    }
};

    const validateForm = () => {
        const errors = [];
        
        if (!contrat.employeur_nom) errors.push("Le nom de l'entreprise est requis");
        if (!contrat.employe_nom) errors.push("Le nom de l'employé est requis");
        if (!contrat.employe_prenom) errors.push("Le prénom de l'employé est requis");
        if (!contrat.date_debut) errors.push("La date de début est requise");
        
        // Vérifier que la date de fin est postérieure à la date de début
        if (contrat.date_debut && contrat.date_fin) {
            if (new Date(contrat.date_fin) < new Date(contrat.date_debut)) {
                errors.push("La date de fin doit être postérieure à la date de début");
            }
        }
        
        return errors;
    };

   const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
        validationErrors.forEach(err => {
            addFixedAlert('error', 'Erreur de validation', err);
        });
        return;
    }

    setSaving(true);
    setError("");

    try {
        const token = localStorage.getItem("token");
        
        // Création du FormData
        const formData = new FormData();
        for (const key in contrat) {
            if (contrat[key] !== null && contrat[key] !== undefined) {
                formData.append(key, contrat[key]);
            }
        }

        const response = await fetch(`${API_BASE_URL}/contrats.php`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
                // ⚠️ NE PAS mettre 'Content-Type' ici → le navigateur le définit avec le boundary
            },
            body: formData
        });

        const result = await response.json();

        if (result.success) {
            setSuccess("Contrat créé avec succès !");
            showToast('success', 'Contrat créé', 'Le contrat a été enregistré avec succès');
            addFixedAlert('success', 'Contrat créé', 'Le contrat a été enregistré avec succès');
            setTimeout(() => {
                navigate('/rh/contrats');
            }, 2000);
        } else {
            setError(result.message || "Erreur lors de la création");
            addFixedAlert('error', 'Erreur', result.message || "Erreur lors de la création du contrat");
        }
    } catch (err) {
        console.error("Erreur:", err);
        setError("Erreur de connexion au serveur");
        addFixedAlert('error', 'Erreur de connexion', err.message || "Impossible de contacter le serveur");
    } finally {
        setSaving(false);
    }
};
    const getFullName = () => {
        return `${contrat.employe_prenom} ${contrat.employe_nom}`.trim() || 'Employé';
    };

    const getTypeContrat = () => {
        return contrat.date_fin ? 'CDD (Contrat à durée déterminée)' : 'CDI (Contrat à durée indéterminée)';
    };

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    
                    {/* Barre d'alerte fixe en haut */}
                    <FixedAlertBar alerts={fixedAlerts} onClose={removeFixedAlert} />

                    {/* En-tête */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-file-copy-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Nouveau contrat</h4>
                                    <p className="text-muted small mb-0">Établir un contrat de travail</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/rh/contrats')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* Toast notification (fixe en bas à droite) */}
                    <ToastNotification 
                        show={toast.show}
                        onClose={hideToast}
                        type={toast.type}
                        title={toast.title}
                        message={toast.message}
                    />

                    {/* Résumé rapide */}
                    {(contrat.employe_nom || contrat.employe_prenom || contrat.employeur_nom) && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4 bg-light">
                            <Card.Body className="py-3">
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="p-2 bg-primary bg-opacity-10 rounded-circle">
                                            <i className="ri-file-copy-line text-primary"></i>
                                        </div>
                                        <div>
                                            <span className="text-muted small">Contrat pour</span>
                                            <div className="fw-semibold">{getFullName()}</div>
                                        </div>
                                    </div>
                                    <div>
                                        <Badge bg={contrat.date_fin ? 'info' : 'primary'} className="rounded-pill px-3 py-2">
                                            <i className="ri-calendar-line me-1"></i>
                                            {getTypeContrat()}
                                        </Badge>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    <Row className="justify-content-center">
                        <Col lg={10}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-file-copy-line me-2"></i>
                                            Formulaire de contrat
                                        </h5>
                                        <Badge className="rounded-pill px-3 py-2" style={{ background: 'white !important', color: '#fff' }}>
                                            <i className="ri-user-line me-1"></i>
                                            {getFullName()}
                                        </Badge>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Form onSubmit={handleSubmit}>
                                        <Row className="g-4">
                                            {/* SECTION 1 : Informations de l'employeur */}
                                            <Col md={12}>
                                                <h6 className="mb-3 fw-semibold" style={{ color: '#21ACCD' }}>
                                                    <i className="ri-building-line me-2"></i>
                                                    Informations de l'employeur
                                                </h6>
                                            </Col>
                                            
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Nom de l'entreprise <span className="text-danger">*</span></Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-building-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="employeur_nom"
                                                            value={contrat.employeur_nom}
                                                            onChange={handleChange}
                                                            required
                                                            className="border-2"
                                                            placeholder="Nom de l'entreprise"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Téléphone</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-phone-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="tel"
                                                            name="employeur_telephone"
                                                            value={contrat.employeur_telephone}
                                                            onChange={handleChange}
                                                            className="border-2"
                                                            placeholder="+241 XX XX XX XX"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Email</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-mail-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="email"
                                                            name="employeur_email"
                                                            value={contrat.employeur_email}
                                                            onChange={handleChange}
                                                            className="border-2"
                                                            placeholder="contact@entreprise.com"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Adresse</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-map-pin-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={2}
                                                            name="employeur_adresse"
                                                            value={contrat.employeur_adresse}
                                                            onChange={handleChange}
                                                            className="border-2"
                                                            placeholder="Adresse complète de l'entreprise"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            {/* SECTION 2 : Informations de l'employé */}
                                            <Col md={12}>
                                                <h6 className="mb-3 mt-2 fw-semibold" style={{ color: '#21ACCD' }}>
                                                    <i className="ri-user-line me-2"></i>
                                                    Informations de l'employé
                                                </h6>
                                            </Col>
                                            
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Nom <span className="text-danger">*</span></Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-user-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="employe_nom"
                                                            value={contrat.employe_nom}
                                                            onChange={handleChange}
                                                            required
                                                            className="border-2"
                                                            placeholder="Nom"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Prénom <span className="text-danger">*</span></Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-user-smile-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="employe_prenom"
                                                            value={contrat.employe_prenom}
                                                            onChange={handleChange}
                                                            required
                                                            className="border-2"
                                                            placeholder="Prénom"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Email</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-mail-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="email"
                                                            name="employe_email"
                                                            value={contrat.employe_email}
                                                            onChange={handleChange}
                                                            className="border-2"
                                                            placeholder="employe@email.com"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Téléphone</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-phone-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="tel"
                                                            name="employe_telephone"
                                                            value={contrat.employe_telephone}
                                                            onChange={handleChange}
                                                            className="border-2"
                                                            placeholder="+241 XX XX XX XX"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Adresse</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-home-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={2}
                                                            name="employe_adresse"
                                                            value={contrat.employe_adresse}
                                                            onChange={handleChange}
                                                            className="border-2"
                                                            placeholder="Adresse de l'employé"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            {/* SECTION 3 : Période du contrat */}
                                            <Col md={12}>
                                                <h6 className="mb-3 mt-2 fw-semibold" style={{ color: '#21ACCD' }}>
                                                    <i className="ri-calendar-line me-2"></i>
                                                    Période du contrat
                                                </h6>
                                            </Col>
                                            
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Date de début <span className="text-danger">*</span></Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-calendar-check-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="date"
                                                            name="date_debut"
                                                            value={contrat.date_debut}
                                                            onChange={handleChange}
                                                            required
                                                            className="border-2"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Date de fin</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-calendar-close-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="date"
                                                            name="date_fin"
                                                            value={contrat.date_fin}
                                                            onChange={handleChange}
                                                            className="border-2"
                                                        />
                                                    </InputGroup>
                                                    <Form.Text className="text-muted">
                                                        Laisser vide pour un contrat à durée indéterminée (CDI)
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>

                                            {/* SECTION 4 : Documents */}
                                            <Col md={12}>
                                                <h6 className="mb-3 mt-2 fw-semibold" style={{ color: '#21ACCD' }}>
                                                    <i className="ri-file-text-line me-2"></i>
                                                    Documents
                                                </h6>
                                            </Col>
                                            
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Lettre de fin de contrat</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-file-pdf-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="file"
                                                            accept=".pdf"
                                                            onChange={(e) => handleFileChange(e, 'lettre_fin_contrat')}
                                                            className="border-2"
                                                        />
                                                    </InputGroup>
                                                    <Form.Text className="text-muted">
                                                        Format PDF (max 5 Mo)
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Certificat de travail</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-file-pdf-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="file"
                                                            accept=".pdf,.doc,.docx"
                                                            onChange={(e) => handleFileChange(e, 'certificat_travail')}
                                                            className="border-2"
                                                        />
                                                    </InputGroup>
                                                    <Form.Text className="text-muted">
                                                        Format PDF, DOC ou DOCX (max 5 Mo)
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>

                                            {/* SECTION 5 : Actions */}
                                            <Col md={12}>
                                                <hr className="my-3" />
                                                <div className="d-flex justify-content-end gap-3">
                                                    <Button 
                                                        variant="outline-secondary" 
                                                        onClick={() => navigate('/rh/contrats')}
                                                        className="rounded-pill px-4"
                                                    >
                                                        Annuler
                                                    </Button>
                                                    <Button 
                                                        type="submit" 
                                                        variant="primary"
                                                        disabled={saving}
                                                        className="rounded-pill px-5"
                                                        style={{ background: '#21ACCD', border: 'none' }}
                                                    >
                                                        {saving ? (
                                                            <>
                                                                <Spinner size="sm" className="me-2" />
                                                                Enregistrement...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="ri-save-line me-2"></i>
                                                                Enregistrer le contrat
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            <style>{`
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: #21ACCD;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                .rounded-3 {
                    border-radius: 12px !important;
                }
                .rounded-pill {
                    border-radius: 50px !important;
                }
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                
                /* Animations pour les notifications */
                @keyframes slideInRight {
                    from {
                        opacity: 0;
                        transform: translateX(100%);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }
                
                @keyframes shrink {
                    from {
                        width: 100%;
                    }
                    to {
                        width: 0%;
                    }
                }
                
                .toast-notification-fixed {
                    animation: slideInRight 0.3s ease-out;
                }
                
                .fixed-alert-bar {
                    position: sticky;
                    top: 70px;
                    z-index: 1000;
                }
                
                /* Animation pour les alertes fixes */
                .fixed-alert-bar .alert {
                    animation: slideInDown 0.3s ease-out;
                }
                
                @keyframes slideInDown {
                    from {
                        opacity: 0;
                        transform: translateY(-100%);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </>
    );
}